import React, { useMemo } from 'react';
import { Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import {
  ArrowDownward as DecliningIcon,
  ArrowUpward as ImprovingIcon,
  Remove as FlatIcon
} from '@mui/icons-material';
import { RUBRIC_DIMENSIONS, RUBRIC_MAX_SCORE } from '../../constants/rubricDimensions';
import { WEAK_AREA_THRESHOLD, CHART_INK } from '../../constants/chartPalette';

export interface DimensionSample {
  /** Per-dimension raw score on the rubric's native 1-5 scale. */
  dimensions: Record<string, number | null>;
}

interface WeakAreasPanelProps {
  /** Sessions oldest-first, so a trend can be read off the order. */
  history: DimensionSample[];
  /** Team-wide per-dimension averages (1-5), for the "vs team" delta. */
  teamAverages?: Record<string, number>;
  /** Who this is about — used in the copy so the panel reads as a sentence. */
  subjectLabel: string;
  maxItems?: number;
}

interface WeakArea {
  key: string;
  label: string;
  average: number;
  percent: number;
  /** Later-half average minus earlier-half. Null when there is too little data. */
  delta: number | null;
  /** Points below the team average, or null when there is no team baseline. */
  vsTeam: number | null;
  sampleCount: number;
}

/** Below this many scored sessions a "trend" is noise, not a signal. */
const MIN_SESSIONS_FOR_TREND = 4;
/** Change smaller than this on a 1-5 scale is not worth calling a direction. */
const FLAT_BAND = 0.25;

/**
 * "Where is this rep lagging" — the question an admin actually opens the page to
 * answer.
 *
 * NOT A CHART, deliberately. A ranked list of three named weaknesses with a
 * direction and a comparison is a set of sentences, and sentences are what a
 * manager acts on. The charts below it show the shape of the data; this says
 * what to do about it.
 *
 * Weakness is measured against the rubric's OWN "soft call" boundary (60%, or
 * 30/50) rather than against an invented threshold or against the rep's peers.
 * That matters: a team can be uniformly weak at Pain With a Price Tag, and a
 * purely relative ranking would report everyone as fine.
 */
const WeakAreasPanel: React.FC<WeakAreasPanelProps> = ({
  history,
  teamAverages,
  subjectLabel,
  maxItems = 3
}) => {
  const weakAreas = useMemo<WeakArea[]>(() => {
    if (!history?.length) return [];

    return RUBRIC_DIMENSIONS.map((dimension) => {
      const scores = history
        .map((h) => h?.dimensions?.[dimension.key])
        .filter((v): v is number => typeof v === 'number');

      if (!scores.length) return null;

      const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;
      const average = mean(scores);

      // First half vs second half. Cruder than a regression and far easier to
      // explain to the person being measured, which matters more here.
      let delta: number | null = null;
      if (scores.length >= MIN_SESSIONS_FOR_TREND) {
        const mid = Math.floor(scores.length / 2);
        delta = mean(scores.slice(mid)) - mean(scores.slice(0, mid));
      }

      const teamAverage = teamAverages?.[dimension.key];
      return {
        key: dimension.key,
        label: dimension.label,
        average,
        percent: (average / RUBRIC_MAX_SCORE) * 100,
        delta,
        vsTeam: typeof teamAverage === 'number' ? average - teamAverage : null,
        sampleCount: scores.length
      };
    })
      .filter((d): d is WeakArea => d !== null)
      .filter((d) => d.percent < WEAK_AREA_THRESHOLD)
      .sort((a, b) => a.percent - b.percent)
      .slice(0, maxItems);
  }, [history, teamAverages, maxItems]);

  if (!history?.length) {
    return null;
  }

  if (!weakAreas.length) {
    return (
      <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', mb: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Weak areas
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Nothing below the soft-call line ({WEAK_AREA_THRESHOLD}%) for {subjectLabel}.
          Every dimension is scoring at or above the standard.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: 1, borderColor: 'divider', mb: 3 }}>
      <Box display="flex" alignItems="baseline" gap={1} mb={0.5}>
        <Typography variant="subtitle2" fontWeight={700}>
          Weak areas
        </Typography>
        <Typography variant="caption" color="text.secondary">
          scoring below {WEAK_AREA_THRESHOLD}% — the rubric's soft-call line
        </Typography>
      </Box>

      <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
        {weakAreas.map((area, index) => (
          <WeakAreaRow key={area.key} area={area} rank={index + 1} />
        ))}
      </Box>
    </Paper>
  );
};

const WeakAreaRow: React.FC<{ area: WeakArea; rank: number }> = ({ area, rank }) => {
  const trend = describeTrend(area.delta);

  return (
    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
      <Typography
        variant="caption"
        sx={{ fontFamily: 'monospace', color: CHART_INK.muted, minWidth: 16 }}
      >
        {rank}
      </Typography>

      <Box flexGrow={1} minWidth={180}>
        <Typography variant="body2" fontWeight={600}>
          {area.label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {area.average.toFixed(1)} / {RUBRIC_MAX_SCORE} across {area.sampleCount}{' '}
          {area.sampleCount === 1 ? 'call' : 'calls'}
        </Typography>
      </Box>

      {/* Direction, with an icon AND a word — never colour alone. */}
      <Tooltip title={trend.explanation}>
        <Chip
          size="small"
          icon={trend.icon}
          label={trend.label}
          sx={{
            bgcolor: 'transparent',
            border: 1,
            borderColor: 'divider',
            color: trend.color,
            '& .MuiChip-icon': { color: trend.color }
          }}
        />
      </Tooltip>

      {area.vsTeam != null && Math.abs(area.vsTeam) >= 0.1 && (
        <Tooltip title="Compared with the team average for this dimension">
          <Typography variant="caption" sx={{ color: CHART_INK.axis, minWidth: 76 }}>
            {area.vsTeam > 0 ? '+' : ''}
            {area.vsTeam.toFixed(1)} vs team
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

/** Direction of travel, as an icon + word + colour (never colour alone). */
function describeTrend(delta: number | null): {
  label: string;
  icon: React.ReactElement;
  color: string;
  explanation: string;
} {
  if (delta == null) {
    return {
      label: 'New',
      icon: <FlatIcon fontSize="small" />,
      color: CHART_INK.axis,
      explanation: `Fewer than ${MIN_SESSIONS_FOR_TREND} scored calls — too little to call a direction.`
    };
  }
  if (delta > FLAT_BAND) {
    return {
      label: 'Improving',
      icon: <ImprovingIcon fontSize="small" />,
      color: '#15803D',
      explanation: `Up ${delta.toFixed(1)} points comparing the later calls with the earlier ones.`
    };
  }
  if (delta < -FLAT_BAND) {
    return {
      label: 'Declining',
      icon: <DecliningIcon fontSize="small" />,
      color: '#BE123C',
      explanation: `Down ${Math.abs(delta).toFixed(1)} points comparing the later calls with the earlier ones.`
    };
  }
  return {
    label: 'Flat',
    icon: <FlatIcon fontSize="small" />,
    color: CHART_INK.axis,
    explanation: 'No meaningful movement between the earlier and later calls.'
  };
}

export default WeakAreasPanel;
